import { toast } from 'sonner';

// Tipos baseados na resposta da API CNPJ.ws
export interface CnpjAtividadePrincipal {
  id: string;
  secao: string;
  divisao: string;
  grupo: string;
  classe: string;
  subclasse: string;
  descricao: string;
}

export interface CnpjSocio {
  cpf_cnpj_socio: string;
  nome: string;
  tipo: string;
  data_entrada: string;
  cpf_representante_legal: string;
  nome_representante: string;
  faixa_etaria: string;
  atualizado_em: string;
  pais_id: string;
  qualificacao_socio: {
    id: number;
    descricao: string;
  };
  qualificacao_representante: string;
  pais: {
    id: string;
    iso2: string;
    iso3: string;
    nome: string;
    comex_id: string;
  };
}

export interface CnpjEstabelecimento {
  cnpj: string;
  cnpj_raiz: string;
  cnpj_ordem: string;
  cnpj_digito_verificador: string;
  tipo: string;
  nome_fantasia: string;
  situacao_cadastral: string;
  data_situacao_cadastral: string;
  data_inicio_atividade: string;
  nome_cidade_exterior: string;
  tipo_logradouro: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cep: string;
  ddd1: string;
  telefone1: string;
  ddd2: string;
  telefone2: string;
  ddd_fax: string;
  fax: string;
  email: string;
  situacao_especial: string;
  data_situacao_especial: string;
  atualizado_em: string;
  atividade_principal: CnpjAtividadePrincipal;
  atividades_secundarias: CnpjAtividadePrincipal[];
  pais: {
    id: string;
    iso2: string;
    iso3: string;
    nome: string;
    comex_id: string;
  };
  estado: {
    id: number;
    nome: string;
    sigla: string;
    ibge_id: number;
  };
  cidade: {
    id: number;
    nome: string;
    ibge_id: number;
    siafi_id: string;
  };
  motivo_situacao_cadastral: string;
  inscricoes_estaduais: Array<{
    inscricao_estadual: string;
    ativo: boolean;
    atualizado_em: string;
    estado: {
      id: number;
      nome: string;
      sigla: string;
      ibge_id: number;
    };
  }>;
}

export interface CnpjResponse {
  cnpj_raiz: string;
  razao_social: string;
  capital_social: string;
  responsavel_federativo: string;
  atualizado_em: string;
  porte: {
    id: string;
    descricao: string;
  };
  natureza_juridica: {
    id: string;
    descricao: string;
  };
  qualificacao_do_responsavel: {
    id: number;
    descricao: string;
  };
  socios: CnpjSocio[];
  simples: {
    simples: string;
    data_opcao_simples: string;
    data_exclusao_simples: string;
    mei: string;
    data_opcao_mei: string;
    data_exclusao_mei: string;
    atualizado_em: string;
  };
  estabelecimento: CnpjEstabelecimento;
}

export const cnpjService = {
  /**
   * Consulta dados de uma empresa pelo CNPJ
   * @param cnpj CNPJ com ou sem formatação
   * @returns Dados completos da empresa
   */
  async lookup(cnpj: string): Promise<CnpjResponse> {
    const cleanCnpj = cnpj.replace(/[^\d]/g, '');

    if (cleanCnpj.length !== 14) {
      throw new Error('CNPJ deve ter 14 dígitos');
    }

    try {
      // Use relative URL so dev proxy (Vite) and production rewrites work consistently.
      const response = await fetch(`/api/cnpj/lookup?cnpj=${cleanCnpj}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.error || `Erro ao consultar CNPJ (${response.status})`;
        throw new Error(errorMsg);
      }

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Resposta inválida da API de CNPJ (esperado JSON).');
      }

      return await response.json();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao consultar CNPJ';
      toast.error(message);
      throw error;
    }
  },
};

/**
 * Formata CNPJ para exibição: 00.000.000/0000-00
 */
export function formatCnpj(cnpj: string): string {
  const clean = cnpj.replace(/[^\d]/g, '');
  if (clean.length !== 14) return cnpj;
  return clean.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    '$1.$2.$3/$4-$5'
  );
}

/**
 * Formata telefone a partir de DDD + número
 */
export function formatPhone(ddd: string, telefone: string): string {
  if (!ddd || !telefone) return '';
  return `(${ddd}) ${telefone}`;
}
